import { logInfo } from "@lib/logger";
import {
  selectUserLocation,
  setCurrentLocationAction,
} from "@store/location/locationReducer";
import { useAppDispatch, useAppSelector } from "@store/store";
import { addToastAction } from "@store/toastReducer";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

/**
 * Wrapper of native js geo location api to return a promise instead of using callbacks.
 */
const getCurrentPositionAsync = (): Promise<GeolocationPosition> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this browser."));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject);
  });
};

/**
 * Get last user location if it exists. Also provides `getUserLocation` to attempt to get initial
 * or latest user location.
 */
const useUserLocation = () => {
  const userLocation = useAppSelector(selectUserLocation);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const getUserLocation = useCallback(async () => {
    let location = userLocation;

    try {
      const position = await getCurrentPositionAsync();
      location = {
        coordinates: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        },
        updatedAt: new Date().toISOString(),
      };
      dispatch(setCurrentLocationAction(location));
    } catch (error) {
      dispatch(
        addToastAction({
          message: t("map.unableToGetLocation"),
          type: "error",
        }),
      );
      logInfo("Unable to get location", error);
    }

    return location;
  }, [dispatch, t, userLocation]);

  return { userLocation, getUserLocation };
};

export { useUserLocation };
